import { useAppConfig } from "../AppConfigProvider";
import { NavigationContainer, useNavigationContainerRef } from "@react-navigation/native";
import { createStackNavigator } from "@react-navigation/stack";
import withAuth from "../hocs/withAuth";
import withCompanionProviders from "../hocs/withCompanionProviders";
import noAuth from "../hocs/noAuth";
import { useMemo, useRef, useState } from "react";
import { routingInstrumentation } from "../utils/sentry";
import { useAuth } from "../hooks/useAuth";
import { UserRoleUI } from "../constants/rolesAndPermissions";
import { StyleSheet, Text, View } from "react-native";
import Loader from "../components/common/status/Loader/Loader";
import { getDefaultAuthenticatedRoute } from "../utils/authRoutes.utils";
import { RootStackParamList, RouteName } from "./navigation.types";
import { useRouteAnnouncer } from "../hooks/useRouteAnnouncer";

export type { RootStackParamList, RouteName } from "./navigation.types";

const Stack = createStackNavigator<RootStackParamList>();

export type RouteConfig<RouteName> = {
  name: RouteName;
  path: string;
  getComponent: () => React.ComponentType<unknown>;
  initialParams?: unknown;
};

const routeConfigStudent = (_isCompanionMode: boolean, _isLMS: boolean): RouteConfig<RouteName>[] =>
  [
    {
      name: "Home",
      path: "home",
      getComponent: () => withAuth(require("../views/HomeView").default),
    },
    {
      name: "Companion Home",
      path: "companion/home",
      getComponent: () => withAuth(withCompanionProviders(require("../views/CompanionHomeView").default)),
    },
    {
      name: "Companion Chat",
      path: "companion/chat",
      getComponent: () => withAuth(withCompanionProviders(require("../views/CompanionView").default)),
    },
    {
      name: "Portfolio",
      path: "portfolio",
      getComponent: () => withAuth(require("../views/PortfolioView").default),
    },
    {
      name: "Journal",
      path: "journal",
      getComponent: () => withAuth(require("../views/JournalView").default),
    },
    {
      name: "Coursework",
      path: "coursework",
      getComponent: () => withAuth(require("../views/SchoolsView").default),
    },
    {
      name: "Activities",
      path: "activities",
      getComponent: () => withAuth(require("../views/SchoolsView").default),
    },
    {
      name: "Course",
      path: "school/course",
      getComponent: () => withAuth(require("../views/CourseView").default),
    },
    {
      name: "Custom School Student Courses",
      path: "student/school",
      getComponent: () => withAuth(require("../views/CustomSchoolStudentCoursesView").default),
    },
    {
      name: "Custom Course Student",
      path: "student/school/course",
      getComponent: () => withAuth(require("../views/CustomCourseStudentView").default),
    },
    {
      name: "Custom Resource Student",
      path: "student/school/course/resource",
      getComponent: () => withAuth(require("../views/CustomResourceStudentView").default),
    },
    {
      name: "Custom Assignment Student",
      path: "student/school/course/assignment",
      getComponent: () => withAuth(require("../views/CustomAssignmentStudentView").default),
    },
    {
      name: "Custom Course AITutor",
      path: "student/school/course/aitutor",
      getComponent: () => withAuth(require("../views/CustomCourseAITutorChatView").default),
    },
    {
      name: "Custom Course Assessment",
      path: "student/school/course/assessment",
      getComponent: () => withAuth(require("../views/CustomCourseAssessmentStudentView").default),
    },
    {
      name: "Onboarding",
      path: "onboarding",
      getComponent: () => withAuth(require("../views/OnboardingView").default),
    },
    {
      name: "Settings",
      path: "settings",
      getComponent: () => withAuth(require("../views/SettingsView").default),
    },
    {
      name: "Invite",
      path: "invite",
      getComponent: () => withAuth(require("../views/InviteView").default),
    },
    {
      name: "Career",
      path: "career",
      getComponent: () => withAuth(require("../views/CareerView").default),
    },
    {
      name: "Degree",
      path: "degree",
      getComponent: () => withAuth(require("../views/DegreeView").default),
    },
    {
      name: "Project",
      path: "project",
      getComponent: () => withAuth(require("../views/ProjectView").default),
    },
    {
      name: "CareerDetails",
      path: "career/details",
      getComponent: () => withAuth(require("../views/CareerDetailView").default),
    },
    {
      name: "ThankYou",
      path: "thank-you",
      getComponent: () => withAuth(require("../views/ThankYouView").default),
    },
    {
      name: "Custom Module Student",
      path: "student/school/course/module",
      getComponent: () => withAuth(require("../views/CustomModuleStudentView").default),
    },
    {
      name: "Resource",
      path: "school/course/resource",
      getComponent: () => withAuth(require("../views/ResourceView").default),
    },
    {
      name: "School",
      path: "school",
      getComponent: () => withAuth(require("../views/SchoolView").default),
    },
    {
      name: "Announcement",
      path: "announcement",
      getComponent: () => withAuth(require("../views/AnnouncementView").default),
    },
    {
      name: "Announcement Detail",
      path: "announcement/detail",
      getComponent: () => withAuth(require("../views/AnnouncementDetailView").default),
    },
    {
      name: "Enrollment",
      path: "student/school/course/enrollment",
      getComponent: () => withAuth(require("../views/EnrollmentView").default),
    },
    {
      name: "Chat Settings",
      path: "companion/settings",
      getComponent: () => withAuth(withCompanionProviders(require("../views/ChatSettingsView").default)),
    },
    {
      name: "To do",
      path: "companion/todo",
      getComponent: () => withAuth(withCompanionProviders(require("../views/TodoView").default)),
    },
    {
      name: "Learning Activities",
      path: "companion/learning-activities",
      getComponent: () => withAuth(withCompanionProviders(require("../views/CompanionActivityView").default)),
    },
    {
      name: "Interactive Learning Environment",
      path: "companion/learning-activity",
      getComponent: () => withAuth(require("../views/LearningActivityView").default),
    },
    {
      name: "Insights",
      path: "student/insights",
      getComponent: () => withAuth(require("../views/InsightsView").default),
    },
  ] as const satisfies RouteConfig<RouteName>[];

const routeConfigFaculty = (
  isCompanionMode: boolean,
  _isLMS: boolean,
  courseModernizerEnabled = false,
  courseModernizerOnlyEnabled = false
): RouteConfig<RouteName>[] =>
  [
    {
      name: "Student Analytics",
      path: "student-analytics",
      getComponent: () =>
        withAuth(require("../views/StudentAnalyticsView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Custom School Faculty Courses",
      path: "school/courses",
      getComponent: () =>
        withAuth(require("../views/CustomSchoolFacultyCoursesView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
          redirectToIfNoPermission: !isCompanionMode ? "Custom School Student Courses" : "Companion Home",
        }),
    },
    {
      name: "Custom Coursework Modules",
      path: "school/course/modules",
      getComponent: () =>
        withAuth(require("../views/CustomCourseworkModulesView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
          redirectToIfNoPermission: !isCompanionMode ? "Custom Course Student" : "Companion Home",
        }),
    },
    {
      name: "Course Settings",
      path: "school/course/settings",
      getComponent: () =>
        withAuth(require("../views/CourseSettingsView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
          redirectToIfNoPermission: !isCompanionMode ? "Custom School Student Courses" : "Companion Home",
        }),
    },
    {
      name: "Faculty",
      path: "faculty",
      getComponent: () =>
        withAuth(require("../views/FacultyView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    ...(courseModernizerEnabled
      ? ([
          {
            name: "Course Modernizer",
            path: "admin/course-modernizer",
            getComponent: () =>
              withAuth(require("../views/adminViews/CourseModernizerView").default, {
                minRequiredRole: UserRoleUI.FACULTY,
                redirectToIfNoPermission: courseModernizerOnlyEnabled ? "Course Modernizer Access Denied" : undefined,
              }),
          },
          {
            name: "Course Modernizer Intake",
            path: "admin/course-modernizer/intake/:externalCourseId",
            getComponent: () =>
              withAuth(require("../views/adminViews/CourseModernizerIntakeView").default, {
                minRequiredRole: UserRoleUI.FACULTY,
                redirectToIfNoPermission: courseModernizerOnlyEnabled ? "Course Modernizer Access Denied" : undefined,
              }),
          },
          {
            name: "Course Modernizer Review",
            path: "admin/course-modernizer/review",
            getComponent: () =>
              withAuth(require("../views/adminViews/CourseEvaluatorReviewView").default, {
                minRequiredRole: UserRoleUI.FACULTY,
                redirectToIfNoPermission: courseModernizerOnlyEnabled ? "Course Modernizer Access Denied" : undefined,
              }),
          },
          {
            name: "Course Modernizer Access Denied",
            path: "admin/course-modernizer/access-denied",
            getComponent: () => withAuth(require("../views/CourseModernizerAccessDeniedView").default),
          },
        ] as const satisfies RouteConfig<RouteName>[])
      : []),
    {
      name: "Nudge Management",
      path: "admin/nudge-management",
      getComponent: () =>
        withAuth(require("../views/adminViews/NudgeManagementView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
  ] as const satisfies RouteConfig<RouteName>[];

const courseModernizerRouteNames = new Set<RouteName>([
  "Course Modernizer",
  "Course Modernizer Intake",
  "Course Modernizer Review",
  "Course Modernizer Access Denied",
]);

const routeConfigCourseModernizer = (
  isCompanionMode: boolean,
  isLMS: boolean,
  courseModernizerEnabled = false,
  courseModernizerOnlyEnabled = false
): RouteConfig<RouteName>[] =>
  routeConfigFaculty(isCompanionMode, isLMS, courseModernizerEnabled, courseModernizerOnlyEnabled).filter((route) =>
    courseModernizerRouteNames.has(route.name)
  );

const routeConfigAdmin = (_isCompanionMode: boolean, _isLMS: boolean): RouteConfig<RouteName>[] =>
  [
    {
      name: "Admin",
      path: "dashboard",
      getComponent: () =>
        withAuth(require("../views/AdminView").default, {
          minRequiredRole: UserRoleUI.ADMIN,
        }),
    },
    {
      name: "Create Tenant",
      path: "dashboard/tenant/create",
      getComponent: () =>
        withAuth(require("../views/CreateTenantView").default, {
          minRequiredRole: UserRoleUI.ADMIN,
        }),
    },
    {
      name: "Tenant Dashboard",
      path: "dashboard/tenant",
      getComponent: () =>
        withAuth(require("../views/TenantDashboardView").default, {
          minRequiredRole: UserRoleUI.ADMIN,
        }),
    },
    {
      name: "Tenant Settings",
      path: "dashboard/tenant/settings",
      getComponent: () =>
        withAuth(require("../views/TenantSettingsDashboardView").default, {
          minRequiredRole: UserRoleUI.ADMIN,
        }),
    },
    {
      name: "OAuth Client Dashboard",
      path: "dashboard/oauth-client",
      getComponent: () =>
        withAuth(require("../views/OAuthClientDashboardView").default, {
          minRequiredRole: UserRoleUI.ADMIN,
        }),
    },
  ] as const satisfies RouteConfig<RouteName>[];

const adminUsersRoute: RouteConfig<RouteName> = {
  name: "Admin Users",
  path: "admin/users",
  getComponent: () =>
    withAuth(require("../views/adminViews/AdminUsersView").default, {
      minRequiredRole: UserRoleUI.AUDITOR,
    }),
};

const routeConfigSchoolAdmin = (
  isCompanionMode: boolean,
  _isLMS: boolean,
  userRole: UserRoleUI,
  hasIntegrationsEnabled: boolean,
  courseModernizerOnlyEnabled = false
): RouteConfig<RouteName>[] => {
  if (courseModernizerOnlyEnabled) {
    const items: RouteConfig<RouteName>[] = [
      {
        name: "Admin Profile",
        path: "admin/profile",
        getComponent: () =>
          withAuth(require("../views/adminViews/AdminProfileView").default, {
            minRequiredRole: UserRoleUI.FACULTY,
          }),
      },
    ];
    if (!isCompanionMode) {
      items.push(adminUsersRoute);
    }
    return items;
  }

  return [
    isCompanionMode
      ? userRole === UserRoleUI.SCHOOL_ADMIN || userRole === UserRoleUI.ADMIN || userRole === UserRoleUI.AUDITOR
        ? {
            name: "Admin Companion Dashboard",
            path: "admin/dashboard",
            getComponent: () =>
              withAuth(require("../views/adminViews/AICompanionDashboardView").default, {
                minRequiredRole: UserRoleUI.AUDITOR,
              }),
          }
        : {
            name: "Faculty Companion Dashboard",
            path: "admin/dashboard",
            getComponent: () =>
              withAuth(require("../views/adminViews/FacultyDashboardCompanionView").default, {
                minRequiredRole: UserRoleUI.FACULTY,
              }),
          }
      : {
          name: "Admin Dashboard",
          path: "admin/dashboard",
          getComponent: () =>
            withAuth(require("../views/adminViews/AdminDashboardView").default, {
              minRequiredRole: UserRoleUI.FACULTY,
            }),
        },
    ...(isCompanionMode
      ? ([
          {
            name: "Safety Support",
            path: "admin/safety-support",
            getComponent: () =>
              withAuth(require("../views/adminViews/SafetySupportView").default, {
                minRequiredRole: UserRoleUI.ADMIN,
              }),
          },
        ] as const satisfies RouteConfig<RouteName>[])
      : []),
    {
      name: "Manage Courses",
      path: "admin/manage-courses",
      getComponent: () =>
        withAuth(require("../views/adminViews/ManageCoursesView").ManageCoursesView, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Teaching",
      path: "admin/teaching",
      getComponent: () =>
        withAuth(require("../views/adminViews/FacultyTeachingView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Degrees & Programs",
      path: "admin/degrees-programs",
      getComponent: () =>
        withAuth(require("../views/adminViews/DegreesProgramsView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Degree Information",
      path: "admin/degree-information",
      getComponent: () =>
        withAuth(require("../views/adminViews/DegreeInformationView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Course Information",
      path: "admin/course-information",
      getComponent: () =>
        withAuth(require("../views/adminViews/CourseInformationView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Course Information For Particular Student",
      path: "admin/course-information/particular-student",
      getComponent: () =>
        withAuth(require("../views/adminViews/CourseInformationParticularStudentView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Students",
      path: "admin/students",
      getComponent: () =>
        withAuth(require("../views/adminViews/StudentsListView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Student Profile",
      path: "admin/students/profile",
      getComponent: () =>
        withAuth(require("../views/adminViews/StudentProfileView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    adminUsersRoute,
    {
      name: "Admin Grading Center",
      path: "admin/grading-center",
      getComponent: () =>
        withAuth(require("../views/adminViews/GradingCenterView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Admin Analytics",
      path: "admin/analytics",
      getComponent: () =>
        withAuth(require("../views/adminViews/AdminAnalyticsView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Admin Settings",
      path: "admin/settings",
      getComponent: () =>
        withAuth(require("../views/adminViews/AdminSettingsView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Admin Profile",
      path: "admin/profile",
      getComponent: () =>
        withAuth(require("../views/adminViews/AdminProfileView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Session Analytics",
      path: "session-analytics",
      getComponent: () =>
        withAuth(require("../views/adminViews/SessionAnalyticsView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "User Session Details",
      path: "session-analytics/user",
      getComponent: () =>
        withAuth(require("../views/adminViews/UserSessionDetailsView").default, {
          minRequiredRole: UserRoleUI.FACULTY,
        }),
    },
    {
      name: "Admin Announcements",
      path: "admin/announcements",
      getComponent: () =>
        withAuth(require("../views/adminViews/AdminAnnouncementsView").default, {
          minRequiredRole: UserRoleUI.SCHOOL_ADMIN,
        }),
    },
    ...(hasIntegrationsEnabled
      ? ([
          {
            name: "Admin Integrations",
            path: "admin/integrations",
            getComponent: () =>
              withAuth(require("../views/adminViews/IntegrationsView").default, {
                minRequiredRole: UserRoleUI.SCHOOL_ADMIN,
              }),
          },
        ] as const satisfies RouteConfig<RouteName>[])
      : []),
  ] as const satisfies RouteConfig<RouteName>[];
};

const routeConfigAuth = (_isCompanionMode: boolean, isLMS: boolean): RouteConfig<RouteName>[] =>
  [
    {
      name: "Login",
      path: "login",
      getComponent: () => noAuth(require("../views/LoginView").default),
      initialParams: { isLMS },
    },
    {
      name: "LoginSuperAdmin",
      path: "admin/login",
      getComponent: () => noAuth(require("../views/LoginView").default),
      initialParams: { isLMS },
    },
    {
      name: "LoginLTI",
      path: "login/lti",
      getComponent: () => noAuth(require("../views/LoginLTIView").default),
    },
    {
      name: "LoginSSO",
      path: "login/sso",
      getComponent: () => noAuth(require("../views/LoginSSOView").default),
    },
    {
      name: "Signup",
      path: "signup",
      getComponent: () => noAuth(require("../views/SignupView").default),
      initialParams: { isLMS },
    },
    {
      name: "Reset Password",
      path: "reset-password",
      getComponent: () => noAuth(require("../views/ResetPasswordView").default),
    },
    {
      name: "Set Password",
      path: "set-password",
      getComponent: () => require("../views/SetPasswordView").default,
    },
    {
      name: "Verify Email",
      path: "verify-email",
      getComponent: () => noAuth(require("../views/VerifyEmailView").default),
    },
    {
      name: "EdlinkSuccess",
      path: "edlink-success",
      getComponent: () => withAuth(require("../views/EdlinkSuccessView").default),
    },
    {
      name: "LmsLaunchSetup",
      path: "lms-launch-setup",
      getComponent: () => require("../containers/lmsLaunchSetup/LmsLaunchSetupContainer").default,
    },
  ] as const satisfies RouteConfig<RouteName>[];

/**
 * LTI activity routes — no withAuth HOC, no standard app layout.
 * Auth is bootstrapped from URL tokens within each view.
 */
const routeConfigLTI = (): RouteConfig<RouteName>[] =>
  [
    {
      name: "LoginLTIILE",
      path: "login/lti-ile",
      getComponent: () => noAuth(require("../views/LoginLTIILEView").default),
    },
    {
      name: "LTIDeepLink",
      path: "deep-link",
      getComponent: () => require("../views/LTIDeepLinkILEView").default,
    },
    {
      name: "LTIILE",
      path: "lti/ile/:activityId",
      getComponent: () => require("../views/LTIILEView").default,
    },
    {
      name: "LTI Edit ILE",
      path: "lti/edit-ile/:activityId",
      getComponent: () => require("../views/LTIEditILEView").default,
    },
    {
      name: "LTI Analytics",
      path: "lti/analytics/:activityId",
      getComponent: () => require("../views/LTIAnalyticsView").default,
    },
    {
      name: "LTIErrorView",
      path: "lti/error",
      getComponent: () => require("../views/LTIErrorView").default,
    },
  ] as const satisfies RouteConfig<RouteName>[];

export const routeConfig: (
  isCompanionMode: boolean,
  isLMS: boolean,
  userRole: UserRoleUI,
  hasIntegrationsEnabled?: boolean,
  courseModernizerEnabled?: boolean,
  courseModernizerOnlyEnabled?: boolean
) => RouteConfig<RouteName>[] = (
  isCompanionMode: boolean,
  isLMS: boolean,
  userRole: UserRoleUI,
  hasIntegrationsEnabled = false,
  courseModernizerEnabled = false,
  courseModernizerOnlyEnabled = false
) => {
  if (courseModernizerOnlyEnabled) {
    return [
      ...routeConfigCourseModernizer(isCompanionMode, isLMS, courseModernizerEnabled, courseModernizerOnlyEnabled),
      ...routeConfigSchoolAdmin(isCompanionMode, isLMS, userRole, hasIntegrationsEnabled, courseModernizerOnlyEnabled),
      ...routeConfigAuth(isCompanionMode, isLMS),
      ...routeConfigLTI(),
    ] as const satisfies RouteConfig<RouteName>[];
  }

  return [
    ...routeConfigStudent(isCompanionMode, isLMS),
    ...routeConfigFaculty(isCompanionMode, isLMS, courseModernizerEnabled, courseModernizerOnlyEnabled),
    ...routeConfigAdmin(isCompanionMode, isLMS),
    ...routeConfigSchoolAdmin(isCompanionMode, isLMS, userRole, hasIntegrationsEnabled, courseModernizerOnlyEnabled),
    ...routeConfigAuth(isCompanionMode, isLMS),
    ...routeConfigLTI(),
  ] as const satisfies RouteConfig<RouteName>[];
};

function AppNavigator() {
  const {
    currentDomain,
    isLMS,
    isCompanionMode,
    hasIntegrationsEnabled,
    courseModernizerEnabled,
    courseModernizerOnlyEnabled,
  } = useAppConfig();
  const navigation = useNavigationContainerRef<RootStackParamList>();
  const componentCacheRef = useRef<Record<string, React.ComponentType<unknown>>>({});
  const [currentRouteName, setCurrentRouteName] = useState<RouteName | null>(null);

  const { userInfo, isAuthenticated, isEmailVerified, otpVerifiedForSession, isAuthReady } = useAuth();
  const { announcement } = useRouteAnnouncer(currentRouteName);

  const isVerified = isEmailVerified && otpVerifiedForSession;
  const shouldUseCourseModernizerOnly = !!courseModernizerEnabled && !!courseModernizerOnlyEnabled;
  const initialRoute = useMemo<RouteName>(() => {
    if (!isAuthenticated) {
      return "Login";
    }
    if (!isVerified) {
      return "Verify Email";
    }
    return getDefaultAuthenticatedRoute({
      userRole: userInfo.userRole,
      isCompanionMode,
      courseModernizerOnlyEnabled: shouldUseCourseModernizerOnly,
    });
  }, [isAuthenticated, isVerified, isCompanionMode, shouldUseCourseModernizerOnly, userInfo.userRole]);

  const routes = routeConfig(
    isCompanionMode,
    isLMS,
    userInfo.userRole,
    hasIntegrationsEnabled,
    courseModernizerEnabled,
    shouldUseCourseModernizerOnly
  );

  const screens = {};

  for (const route of routes) {
    screens[route.name] = route.path;
  }

  const linking = {
    prefixes: [`https://${currentDomain}`, "primer://"],
    config: {
      screens: screens,
    },
  };

  const currentPath = typeof window !== "undefined" ? window.location.pathname : "";
  const ltiRoutes = useMemo(() => routeConfigLTI(), []);
  const isLTIPath = useMemo(
    () =>
      ltiRoutes.some((r) => {
        const routePath = "/" + r.path;
        if (r.path.includes("/:")) {
          const prefix = routePath.split("/:")[0] + "/";
          return currentPath.startsWith(prefix);
        }
        return currentPath === routePath || currentPath.startsWith(routePath + "/");
      }),
    [currentPath, ltiRoutes]
  );

  if (!isAuthReady && !isLTIPath) {
    return (
      <View style={styles.loaderContainer}>
        <Loader />
      </View>
    );
  }

  return (
    <>
      <NavigationContainer
        linking={linking}
        ref={navigation}
        onReady={() => {
          routingInstrumentation.registerNavigationContainer(navigation);
          setCurrentRouteName((navigation.getCurrentRoute()?.name as RouteName | undefined) ?? null);
        }}
        onStateChange={() => {
          setCurrentRouteName((navigation.getCurrentRoute()?.name as RouteName | undefined) ?? null);
        }}>
        <Stack.Navigator key={initialRoute} initialRouteName={initialRoute} screenOptions={{ headerShown: false }}>
          {routes.map((route) => (
            <Stack.Screen
              key={route.name}
              name={route.name as keyof RootStackParamList}
              getComponent={() => {
                const cacheKey = String(route.name);
                const cached = componentCacheRef.current[cacheKey];
                if (cached) {
                  return cached;
                }
                const resolved = route.getComponent();
                componentCacheRef.current[cacheKey] = resolved;
                return resolved;
              }}
              initialParams={route.initialParams}
            />
          ))}
        </Stack.Navigator>
        <View
          aria-live="assertive"
          aria-atomic="true"
          accessibilityLiveRegion="assertive"
          pointerEvents="none"
          style={styles.routeAnnouncer}>
          <Text>{announcement}</Text>
        </View>
      </NavigationContainer>

      {(!isAuthReady || userInfo.userRole === undefined) && (
        <View style={styles.loaderContainer}>
          <Loader />
        </View>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  routeAnnouncer: {
    position: "absolute",
    left: -10000,
    top: -10000,
    width: 1,
    height: 1,
    overflow: "hidden",
  },
});

export default AppNavigator;
